import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Users, IndianRupee, MousePointerClick, ShoppingCart, TrendingUp, CheckCircle2,
  Clock, XCircle, Search, ChevronDown, X, Settings, Shield, Ban, UserCheck, Eye,
  Wallet, FileText, Copy
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { API_BASE as API } from "@/config";
import axios from "axios";
import { CorrectionsTable } from "@/components/CorrectionsTable";

const fadeIn = { hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0, transition: { duration: 0.3 } } };

function AffStatCard({ icon, label, value, color }) {
  return (
    <Card className="bg-white border border-gray-100 shadow-sm rounded-2xl">
      <CardContent className="p-4">
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center mb-2 ${color}`}>{icon}</div>
        <p className="text-xl font-bold text-gray-900 tabular-nums" style={{ fontFamily: 'Space Grotesk' }}>{value}</p>
        <p className="text-gray-500 text-xs mt-0.5">{label}</p>
      </CardContent>
    </Card>
  );
}

export default function AdminAffiliatePanel() {
  const [subTab, setSubTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [config, setConfig] = useState(null);
  const [influencers, setInfluencers] = useState([]);
  const [infTotal, setInfTotal] = useState(0);
  const [commissions, setCommissions] = useState([]);
  const [commTotal, setCommTotal] = useState(0);
  const [payouts, setPayouts] = useState([]);
  const [searchQ, setSearchQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedInf, setSelectedInf] = useState(null);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [payoutData, setPayoutData] = useState({ influencer_id: "", commission_ids: [], amount: 0, method: "upi", reference: "", notes: "" });
  const [configForm, setConfigForm] = useState({ program_active: true, commission_type: "percentage", commission_rate: 10, min_payout: 50000 });

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/affiliate/stats`);
      setStats(res.data);
    } catch { /* ignore */ }
  }, []);

  const fetchConfig = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/affiliate/config`);
      setConfig(res.data);
      setConfigForm({
        program_active: res.data.program_active ?? true,
        commission_type: res.data.commission_type || "percentage",
        commission_rate: res.data.commission_rate || 10,
        min_payout: res.data.min_payout || 50000,
      });
    } catch { /* ignore */ }
  }, []);

  const fetchInfluencers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/affiliate/influencers`, {
        params: { search: searchQ, status: statusFilter, limit: 50 }
      });
      setInfluencers(res.data.influencers || []);
      setInfTotal(res.data.total || 0);
    } catch { /* ignore */ }
  }, [searchQ, statusFilter]);

  const fetchCommissions = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/affiliate/commissions`, { params: { limit: 50 } });
      setCommissions(res.data.commissions || []);
      setCommTotal(res.data.total || 0);
    } catch { /* ignore */ }
  }, []);

  const fetchPayouts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/affiliate/payouts`, { params: { limit: 50 } });
      setPayouts(res.data.payouts || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    fetchStats();
    fetchConfig();
    fetchInfluencers();
    fetchCommissions();
    fetchPayouts();
  }, [fetchStats, fetchConfig, fetchInfluencers, fetchCommissions, fetchPayouts]);

  const updateInfluencerStatus = async (infId, status) => {
    try {
      await axios.post(`${API}/admin/affiliate/influencers/${infId}/status?status=${status}`);
      toast.success(`Influencer ${status}`);
      fetchInfluencers();
      fetchStats();
    } catch { toast.error("Update failed"); }
  };

  const reverseCommission = async (commId) => {
    if (!window.confirm("Reverse this commission? This cannot be undone.")) return;
    try {
      await axios.post(`${API}/admin/affiliate/commissions/${commId}/reverse`);
      toast.success("Commission reversed");
      fetchCommissions();
      fetchStats();
      fetchInfluencers();
    } catch (err) { toast.error(err?.response?.data?.detail || "Reverse failed"); }
  };

  const saveConfig = async () => {
    try {
      await axios.post(`${API}/admin/affiliate/config`, configForm);
      toast.success("Config saved");
      setShowConfigModal(false);
      fetchConfig();
    } catch { toast.error("Save failed"); }
  };

  const openPayoutModal = async (inf) => {
    // Fetch fresh pending commissions from server (not stale local state)
    try {
      const res = await axios.get(`${API}/admin/affiliate/commissions`, {
        params: { influencer_id: inf.influencer_id, status: "pending", limit: 100 }
      });
      const pendingComms = res.data.commissions || [];
      const totalAmount = pendingComms.reduce((s, c) => s + c.commission_amount, 0);
      if (pendingComms.length === 0) {
        toast.error("No pending commissions for this influencer");
        return;
      }
      setPayoutData({
        influencer_id: inf.influencer_id,
        commission_ids: pendingComms.map(c => c.commission_id),
        amount: totalAmount,
        method: "upi",
        reference: "",
        notes: "",
      });
      setShowPayoutModal(true);
    } catch {
      toast.error("Failed to load pending commissions");
    }
  };

  const createPayout = async () => {
    if (payoutData.commission_ids.length === 0) return toast.error("No pending commissions to pay");
    if (!payoutData.reference) return toast.error("Please enter a payment reference");
    try {
      await axios.post(`${API}/admin/affiliate/payouts/create`, payoutData);
      toast.success("Payout recorded");
      setShowPayoutModal(false);
      fetchCommissions();
      fetchPayouts();
      fetchInfluencers();
      fetchStats();
    } catch (err) { toast.error(err?.response?.data?.detail || "Payout failed"); }
  };

  const openInfluencerDetail = async (infId) => {
    try {
      const res = await axios.get(`${API}/admin/affiliate/influencers/${infId}`);
      setSelectedInf(res.data);
    } catch { toast.error("Failed to load details"); }
  };

  return (
    <div className="space-y-5" data-testid="admin-affiliate-panel">
      {/* Sub-tabs */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex gap-1 bg-gray-100 p-1 rounded-lg max-w-full overflow-x-auto">
          {["overview", "influencers", "commissions", "payouts", "corrections"].map(t => (
            <button key={t} onClick={() => setSubTab(t)}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all capitalize ${subTab === t ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
              data-testid={`aff-tab-${t}`}>
              {t}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowConfigModal(true)} className="text-xs rounded-lg gap-1" data-testid="aff-config-btn">
            <Settings className="w-3 h-3" /> Config
          </Button>
          <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold ${config?.program_active ? "bg-green-50 text-green-600" : "bg-red-50 text-red-600"}`}>
            {config?.program_active ? "Program Active" : "Program Inactive"}
          </span>
        </div>
      </div>

      {/* ===== OVERVIEW ===== */}
      {subTab === "overview" && stats && (
        <motion.div initial="hidden" animate="visible" variants={{ visible: { transition: { staggerChildren: 0.05 } } }}
          className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <motion.div variants={fadeIn}><AffStatCard icon={<Users className="w-4 h-4 text-blue-500" />} label="Total Influencers" value={stats.total_influencers} color="bg-blue-50" /></motion.div>
          <motion.div variants={fadeIn}><AffStatCard icon={<Clock className="w-4 h-4 text-yellow-500" />} label="Pending Approval" value={stats.pending_influencers} color="bg-yellow-50" /></motion.div>
          <motion.div variants={fadeIn}><AffStatCard icon={<MousePointerClick className="w-4 h-4 text-indigo-500" />} label="Total Clicks" value={stats.total_clicks} color="bg-indigo-50" /></motion.div>
          <motion.div variants={fadeIn}><AffStatCard icon={<ShoppingCart className="w-4 h-4 text-green-500" />} label="Referral Orders" value={stats.total_referral_orders} color="bg-green-50" /></motion.div>
          <motion.div variants={fadeIn}><AffStatCard icon={<TrendingUp className="w-4 h-4 text-purple-500" />} label="Total Commission" value={`₹${stats.total_commission}`} color="bg-purple-50" /></motion.div>
          <motion.div variants={fadeIn}><AffStatCard icon={<Clock className="w-4 h-4 text-orange-500" />} label="Pending Commission" value={`₹${stats.pending_commission}`} color="bg-orange-50" /></motion.div>
          <motion.div variants={fadeIn}><AffStatCard icon={<CheckCircle2 className="w-4 h-4 text-emerald-500" />} label="Paid Commission" value={`₹${stats.paid_commission}`} color="bg-emerald-50" /></motion.div>
          <motion.div variants={fadeIn}><AffStatCard icon={<UserCheck className="w-4 h-4 text-teal-500" />} label="Active Influencers" value={stats.approved_influencers} color="bg-teal-50" /></motion.div>
        </motion.div>
      )}

      {/* ===== INFLUENCERS ===== */}
      {subTab === "influencers" && (
        <>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input data-testid="aff-inf-search" placeholder="Search influencers..." value={searchQ}
                onChange={e => setSearchQ(e.target.value)}
                className="pl-10 bg-white border-gray-200 rounded-xl h-10" />
            </div>
            <div className="flex gap-1.5 flex-wrap">
              {["", "pending", "approved", "rejected", "deactivated"].map(f => (
                <button key={f} onClick={() => setStatusFilter(f)}
                  className={`px-2.5 py-1.5 rounded-lg text-[11px] font-semibold transition-all capitalize ${statusFilter === f ? "bg-amber-100 text-amber-700" : "bg-white text-gray-500 border border-gray-200"}`}>
                  {f || "All"}
                </button>
              ))}
            </div>
          </div>

          <p className="text-xs text-gray-400">{infTotal} influencers</p>

          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden" data-testid="aff-inf-table">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50/50">
                    {["Name", "Email", "Code", "Status", "Clicks", "Orders", "Commission", "Pending", "Actions"].map(h => (
                      <th key={h} className="text-left px-3 py-2.5 text-xs font-semibold text-gray-500 whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {influencers.map((inf, i) => (
                    <tr key={inf.influencer_id} className="border-b border-gray-50 hover:bg-amber-50/30 transition-colors" data-testid={`aff-inf-row-${i}`}>
                      <td className="px-3 py-2.5 font-semibold text-gray-900 whitespace-nowrap text-xs">{inf.name}</td>
                      <td className="px-3 py-2.5 text-gray-500 text-xs">{inf.email}</td>
                      <td className="px-3 py-2.5">
                        <code className="bg-gray-100 text-amber-700 px-1.5 py-0.5 rounded text-[10px] font-bold">{inf.referral_code}</code>
                      </td>
                      <td className="px-3 py-2.5">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold capitalize ${
                          inf.status === "approved" ? "bg-green-50 text-green-600" :
                          inf.status === "pending" ? "bg-yellow-50 text-yellow-600" :
                          inf.status === "rejected" ? "bg-red-50 text-red-600" :
                          "bg-gray-100 text-gray-500"
                        }`}>{inf.status}</span>
                      </td>
                      <td className="px-3 py-2.5 text-xs text-gray-600 tabular-nums">{inf.total_clicks}</td>
                      <td className="px-3 py-2.5 text-xs text-gray-600 tabular-nums">{inf.live_total_orders ?? inf.total_orders}</td>
                      <td className="px-3 py-2.5 text-xs font-semibold text-gray-900 tabular-nums">₹{((inf.live_total_commission ?? inf.total_commission) / 100).toFixed(0)}</td>
                      <td className="px-3 py-2.5 text-xs text-orange-600 tabular-nums">₹{((inf.live_pending_commission ?? 0) / 100).toFixed(0)}</td>
                      <td className="px-3 py-2.5">
                        <div className="flex gap-1">
                          <button onClick={() => openInfluencerDetail(inf.influencer_id)} className="p-1 text-gray-400 hover:text-blue-500 transition-colors" title="View"><Eye className="w-3.5 h-3.5" /></button>
                          {inf.status === "pending" && (
                            <>
                              <button onClick={() => updateInfluencerStatus(inf.influencer_id, "approved")} className="p-1 text-gray-400 hover:text-green-500 transition-colors" title="Approve"><UserCheck className="w-3.5 h-3.5" /></button>
                              <button onClick={() => updateInfluencerStatus(inf.influencer_id, "rejected")} className="p-1 text-gray-400 hover:text-red-500 transition-colors" title="Reject"><XCircle className="w-3.5 h-3.5" /></button>
                            </>
                          )}
                          {inf.status === "approved" && (
                            <>
                              <button onClick={() => openPayoutModal(inf)} className="p-1 text-gray-400 hover:text-green-500 transition-colors" title="Pay"><Wallet className="w-3.5 h-3.5" /></button>
                              <button onClick={() => updateInfluencerStatus(inf.influencer_id, "deactivated")} className="p-1 text-gray-400 hover:text-red-500 transition-colors" title="Deactivate"><Ban className="w-3.5 h-3.5" /></button>
                            </>
                          )}
                          {inf.status === "deactivated" && (
                            <button onClick={() => updateInfluencerStatus(inf.influencer_id, "approved")} className="p-1 text-gray-400 hover:text-green-500 transition-colors" title="Reactivate"><UserCheck className="w-3.5 h-3.5" /></button>
                          )}
                          {inf.status === "rejected" && (
                            <button onClick={() => updateInfluencerStatus(inf.influencer_id, "approved")} className="p-1 text-gray-400 hover:text-green-500 transition-colors" title="Approve"><UserCheck className="w-3.5 h-3.5" /></button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {influencers.length === 0 && (
                    <tr><td colSpan={9} className="text-center text-gray-400 py-8 text-sm">No influencers found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* ===== COMMISSIONS ===== */}
      {subTab === "commissions" && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden" data-testid="aff-comm-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/50">
                  {["Influencer", "Code", "Order Amount", "Commission", "Type", "Status", "Date", "Actions"].map(h => (
                    <th key={h} className="text-left px-3 py-2.5 text-xs font-semibold text-gray-500 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {commissions.map((c, i) => (
                  <tr key={c.commission_id} className="border-b border-gray-50" data-testid={`aff-comm-row-${i}`}>
                    <td className="px-3 py-2.5 text-xs font-semibold text-gray-900">{c.influencer_name}</td>
                    <td className="px-3 py-2.5"><code className="bg-gray-100 text-amber-700 px-1.5 py-0.5 rounded text-[10px] font-bold">{c.influencer_ref_code}</code></td>
                    <td className="px-3 py-2.5 text-xs text-gray-600 tabular-nums">₹{(c.order_amount / 100).toFixed(0)}</td>
                    <td className="px-3 py-2.5 text-xs font-bold text-gray-900 tabular-nums">₹{(c.commission_amount / 100).toFixed(0)}</td>
                    <td className="px-3 py-2.5 text-xs text-gray-500">{c.commission_type === "percentage" ? `${c.commission_rate}%` : `₹${(c.commission_rate/100).toFixed(0)} fixed`}</td>
                    <td className="px-3 py-2.5">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold capitalize ${
                        c.status === "paid" ? "bg-green-50 text-green-600" : c.status === "pending" ? "bg-yellow-50 text-yellow-600" : "bg-red-50 text-red-600"
                      }`}>{c.status}</span>
                    </td>
                    <td className="px-3 py-2.5 text-xs text-gray-400">{new Date(c.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</td>
                    <td className="px-3 py-2.5">
                      {c.status === "pending" && (
                        <button onClick={() => reverseCommission(c.commission_id)} className="text-[10px] text-red-500 hover:text-red-700 font-semibold" data-testid={`reverse-comm-${i}`}>Reverse</button>
                      )}
                    </td>
                  </tr>
                ))}
                {commissions.length === 0 && (
                  <tr><td colSpan={8} className="text-center text-gray-400 py-8 text-sm">No commissions yet</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ===== PAYOUTS ===== */}
      {subTab === "payouts" && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden" data-testid="aff-payout-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/50">
                  {["Influencer", "Amount", "Method", "Reference", "Notes", "Commissions", "Date"].map(h => (
                    <th key={h} className="text-left px-3 py-2.5 text-xs font-semibold text-gray-500 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {payouts.map((p, i) => (
                  <tr key={p.payout_id} className="border-b border-gray-50" data-testid={`aff-payout-row-${i}`}>
                    <td className="px-3 py-2.5 text-xs font-semibold text-gray-900">{p.influencer_name}</td>
                    <td className="px-3 py-2.5 text-xs font-bold text-green-600 tabular-nums">₹{(p.amount / 100).toFixed(0)}</td>
                    <td className="px-3 py-2.5 text-xs text-gray-600 uppercase">{p.method}</td>
                    <td className="px-3 py-2.5 text-xs text-gray-500 max-w-[120px] truncate">{p.reference || "-"}</td>
                    <td className="px-3 py-2.5 text-xs text-gray-400 max-w-[150px] truncate">{p.notes || "-"}</td>
                    <td className="px-3 py-2.5 text-xs text-gray-500">{p.commission_ids?.length || 0} items</td>
                    <td className="px-3 py-2.5 text-xs text-gray-400">{new Date(p.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
                  </tr>
                ))}
                {payouts.length === 0 && (
                  <tr><td colSpan={7} className="text-center text-gray-400 py-8 text-sm">No payouts recorded yet</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {subTab === "corrections" && <CorrectionsTable />}

      {/* ===== CONFIG MODAL ===== */}
      <AnimatePresence>
        {showConfigModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center px-4" onClick={() => setShowConfigModal(false)}>
            <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
              className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6" onClick={e => e.stopPropagation()} data-testid="aff-config-modal">
              <div className="flex items-center justify-between mb-5">
                <h3 className="font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk' }}>Affiliate Config</h3>
                <button onClick={() => setShowConfigModal(false)} className="text-gray-400 hover:text-gray-700"><X className="w-4 h-4" /></button>
              </div>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700">Program Active</label>
                  <button onClick={() => setConfigForm(p => ({ ...p, program_active: !p.program_active }))}
                    className={`w-11 h-6 rounded-full transition-colors ${configForm.program_active ? "bg-green-500" : "bg-gray-300"}`} data-testid="aff-config-toggle">
                    <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${configForm.program_active ? "translate-x-5.5 ml-[22px]" : "translate-x-0.5 ml-[2px]"}`} />
                  </button>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Commission Type</label>
                  <div className="flex gap-2">
                    {["percentage", "fixed"].map(t => (
                      <button key={t} onClick={() => setConfigForm(p => ({ ...p, commission_type: t }))}
                        className={`flex-1 py-2 rounded-lg text-xs font-semibold capitalize ${configForm.commission_type === t ? "bg-amber-100 text-amber-700" : "bg-gray-100 text-gray-500"}`}>
                        {t}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">
                    {configForm.commission_type === "percentage" ? "Commission %" : "Fixed Amount (paise)"}
                  </label>
                  <Input type="number" value={configForm.commission_rate}
                    onChange={e => setConfigForm(p => ({ ...p, commission_rate: parseFloat(e.target.value) || 0 }))}
                    className="bg-gray-50 border-gray-200 rounded-xl" data-testid="aff-config-rate" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Min Payout (paise)</label>
                  <Input type="number" value={configForm.min_payout}
                    onChange={e => setConfigForm(p => ({ ...p, min_payout: parseInt(e.target.value) || 0 }))}
                    className="bg-gray-50 border-gray-200 rounded-xl" />
                </div>
                <Button onClick={saveConfig} className="w-full bg-amber-500 hover:bg-amber-600 text-white rounded-xl" data-testid="aff-config-save">Save Config</Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ===== PAYOUT MODAL ===== */}
      <AnimatePresence>
        {showPayoutModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center px-4" onClick={() => setShowPayoutModal(false)}>
            <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
              className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6" onClick={e => e.stopPropagation()} data-testid="aff-payout-modal">
              <div className="flex items-center justify-between mb-5">
                <h3 className="font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk' }}>Record Payout</h3>
                <button onClick={() => setShowPayoutModal(false)} className="text-gray-400 hover:text-gray-700"><X className="w-4 h-4" /></button>
              </div>
              <div className="space-y-4">
                <div className="bg-amber-50 rounded-xl p-3 text-center">
                  <p className="text-xs text-gray-500">Pending Commissions</p>
                  <p className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk' }}>₹{(payoutData.amount / 100).toFixed(0)}</p>
                  <p className="text-[10px] text-gray-400">{payoutData.commission_ids.length} commission(s)</p>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Payment Method</label>
                  <div className="flex gap-2">
                    {["upi", "bank", "other"].map(m => (
                      <button key={m} onClick={() => setPayoutData(p => ({ ...p, method: m }))}
                        className={`flex-1 py-2 rounded-lg text-xs font-semibold uppercase ${payoutData.method === m ? "bg-amber-100 text-amber-700" : "bg-gray-100 text-gray-500"}`}>
                        {m}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Transaction Reference *</label>
                  <Input placeholder="UPI Ref / Transaction ID" value={payoutData.reference}
                    onChange={e => setPayoutData(p => ({ ...p, reference: e.target.value }))}
                    className="bg-gray-50 border-gray-200 rounded-xl" data-testid="aff-payout-ref" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Notes (optional)</label>
                  <Input placeholder="Any notes..." value={payoutData.notes}
                    onChange={e => setPayoutData(p => ({ ...p, notes: e.target.value }))}
                    className="bg-gray-50 border-gray-200 rounded-xl" />
                </div>
                <Button onClick={createPayout} className="w-full bg-green-500 hover:bg-green-600 text-white rounded-xl" data-testid="aff-payout-confirm">
                  Mark as Paid — ₹{(payoutData.amount / 100).toFixed(0)}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ===== INFLUENCER DETAIL MODAL ===== */}
      <AnimatePresence>
        {selectedInf && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-start justify-center pt-8 px-4 overflow-y-auto" onClick={() => setSelectedInf(null)}>
            <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
              className="bg-white rounded-2xl shadow-xl w-full max-w-lg mb-8 overflow-hidden" onClick={e => e.stopPropagation()} data-testid="aff-inf-detail-modal">
              <div className="bg-gradient-to-r from-gray-900 to-gray-800 px-5 py-4 flex items-center justify-between">
                <div>
                  <h3 className="text-base font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>{selectedInf.influencer.name}</h3>
                  <p className="text-gray-400 text-xs">{selectedInf.influencer.email} · {selectedInf.influencer.phone}</p>
                </div>
                <button onClick={() => setSelectedInf(null)} className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="p-5 space-y-4">
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { label: "Status", value: selectedInf.influencer.status },
                    { label: "Code", value: selectedInf.influencer.referral_code },
                    { label: "UPI", value: selectedInf.influencer.upi_id || "N/A" },
                    { label: "Clicks", value: selectedInf.influencer.total_clicks },
                    { label: "Orders", value: selectedInf.influencer.live_total_orders ?? selectedInf.influencer.total_orders },
                    { label: "Sales", value: `₹${((selectedInf.influencer.live_total_sales ?? selectedInf.influencer.total_sales) / 100).toFixed(0)}` },
                    { label: "Total Comm.", value: `₹${((selectedInf.influencer.live_total_commission ?? selectedInf.influencer.total_commission) / 100).toFixed(0)}` },
                    { label: "Paid", value: `₹${((selectedInf.influencer.live_paid_commission ?? selectedInf.influencer.paid_commission) / 100).toFixed(0)}` },
                    { label: "Pending", value: `₹${((selectedInf.influencer.live_pending_commission ?? 0) / 100).toFixed(0)}` },
                  ].map((item, i) => (
                    <div key={i} className="bg-gray-50 rounded-lg p-2">
                      <p className="text-[10px] text-gray-400 uppercase">{item.label}</p>
                      <p className="text-xs font-semibold text-gray-900 capitalize">{item.value}</p>
                    </div>
                  ))}
                </div>
                {selectedInf.commissions.length > 0 && (
                  <div>
                    <h4 className="text-xs font-bold text-gray-900 mb-2">Recent Commissions</h4>
                    <div className="space-y-1 max-h-40 overflow-y-auto">
                      {selectedInf.commissions.slice(0, 10).map(c => (
                        <div key={c.commission_id} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
                          <span className="text-xs text-gray-700">₹{(c.commission_amount / 100).toFixed(0)} on ₹{(c.order_amount / 100).toFixed(0)}</span>
                          <span className={`text-[10px] font-bold capitalize ${c.status === "paid" ? "text-green-600" : c.status === "pending" ? "text-yellow-600" : "text-red-500"}`}>{c.status}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
